import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    ElementRef,
    Input,
    HostBinding,
    OnInit,
    OnChanges,
    Renderer2,
    inject
} from "@angular/core";
import { toBoolean } from "@logex/framework/utilities";
import { LgSimpleChanges } from "@logex/framework/types";
import { LgIconRemappingService } from "./lg-icon-remapping.service";
import { NgClass } from "@angular/common";

@Component({
    selector: "lg-icon",
    template: `
        <svg viewBox="0 0 24 24" [ngClass]="svgClass">
            <use [attr.xlink:href]="'#' + icon"></use>
        </svg>
    `,
    imports: [NgClass],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        "(keydown.enter)": "_onActivationKey($event)",
        "(keydown.space)": "_onActivationKey($event)"
    }
})
export class LgIconComponent implements OnChanges, OnInit {
    private _elementRef = inject(ElementRef);
    private _iconRemapper = inject(LgIconRemappingService);
    private _renderer = inject(Renderer2);

    /**
     * Specifies icon.
     */
    @Input({ required: true }) set icon(value: string | null | undefined) {
        this._icon = this._iconRemapper.remap(value ?? "");
    }

    get icon(): string | null {
        return this._icon;
    }

    /**
     * Specifies if icon is clickable.
     *
     * @default false
     */
    @Input()
    set clickable(value: boolean) {
        this._clickable = toBoolean(value);
    }

    get clickable(): boolean {
        return this._clickable;
    }

    /**
     * Specifies if the icon takes part in the tab order. When not set, it follows `clickable`.
     *
     * Unlike `clickable`, this only makes the icon focusable - it does not activate it on
     * Enter/Space. Use it for icons whose keyboard handling is owned by a parent component.
     */
    @Input()
    set focusable(value: boolean) {
        this._focusable = toBoolean(value);
    }

    get focusable(): boolean {
        return this._focusable ?? this._clickable;
    }

    /**
     * Accessible name of the icon. Rendered as `aria-label`.
     */
    @Input() ariaLabel?: string | null;

    /**
     * Specifies if icon is displayed inline.
     *
     * @default false
     */
    @Input()
    set inline(value: boolean) {
        this._inline = toBoolean(value);
    }

    get inline(): boolean {
        return this._inline;
    }

    /**
     * Icon width (in pixels).
     */
    @Input()
    set width(value: number | string) {
        this._width = this._parseSize(value);
        if (this._height === null) this._height = this._width;
    }

    get width(): string | number | null {
        return this._width;
    }

    /**
     * Icon height (in pixels).
     */
    @Input()
    set height(value: number | string) {
        this._height = this._parseSize(value);
        if (this._width === null) this._width = this._height;
    }

    get height(): string | number | null {
        return this._width;
    }

    /**
     * Icon size
     */
    @Input() size: "regular" | "medium" = "regular";

    /**
     * Icon type
     */
    @Input("type") type:
        "regular" | "success" | "warning" | "info" | "error" | "highlighted" | "disabled" =
        "regular";

    /**
     * Applies css class to icon host.
     */
    @Input() class?: string | undefined;

    /**
     * Applies css class to icon svg.
     */
    @Input() svgClass?: any;

    /**
     * Data attribute for testing purposes.
     * Defaults to "icon-{icon-name}" format.
     */
    @Input() dataTestId?: string;

    @HostBinding("style.width") _width: string | number | null = null;
    @HostBinding("style.height") _height: string | number | null = null;
    @HostBinding("class") _composedClass!: string;
    @HostBinding("attr.data-testid") get _dataTestId(): string {
        return this.dataTestId ?? `icon-${this._icon}`;
    }

    _icon = "";

    private _clickable = false;
    private _focusable: boolean | null = null;
    private _inline = false;
    private _hasExternalTabIndex: boolean | null = null;
    private _ownsFocusAttributes = false;
    private _ownsAriaLabel = false;

    /**
     * Enter/Space activate the icon by dispatching a click, so that the `(click)` handlers of the
     * host template run exactly as they do for the mouse (including their own disabled guards).
     * Only `clickable` icons are activated - `focusable`-only icons leave the keys to their parent.
     */
    _onActivationKey(event: KeyboardEvent): void {
        if (!this._clickable) return;

        event.preventDefault();
        event.stopPropagation();
        this._elementRef.nativeElement.click();
    }

    ngOnInit(): void {
        if (this._composedClass === undefined) this._composeClass();
        this._updateFocusAttributes();
    }

    ngOnChanges(changes: LgSimpleChanges<LgIconComponent>): void {
        if (changes.class || changes.size || changes.clickable || changes.type) {
            this._composeClass();
        }
        if (changes.clickable || changes.focusable || changes.ariaLabel) {
            this._updateFocusAttributes();
        }
    }

    /**
     * The attributes are written imperatively rather than through host bindings, so that a call site
     * which puts its own `tabindex` on the element keeps it - a host binding would compete with
     * theirs on every change detection run. That matters for the roving tab index pattern, where
     * the call site deliberately sets `tabindex="-1"` and navigates with `lgArrowNavigation`
     * (see `lg-flexible-layouts-item`).
     */
    private _updateFocusAttributes(): void {
        const element = this._elementRef.nativeElement;

        this._hasExternalTabIndex ??= element.hasAttribute("tabindex");

        if (this.focusable) {
            if (!this._hasExternalTabIndex) {
                this._renderer.setAttribute(element, "tabindex", "0");
            }
            this._renderer.setAttribute(element, "role", "button");
            this._ownsFocusAttributes = true;
        } else if (this._ownsFocusAttributes) {
            if (!this._hasExternalTabIndex) {
                this._renderer.removeAttribute(element, "tabindex");
            }
            this._renderer.removeAttribute(element, "role");
            this._ownsFocusAttributes = false;
        }

        if (this.ariaLabel) {
            this._renderer.setAttribute(element, "aria-label", this.ariaLabel);
            this._ownsAriaLabel = true;
        } else if (this._ownsAriaLabel) {
            this._renderer.removeAttribute(element, "aria-label");
            this._ownsAriaLabel = false;
        }
    }

    protected _getMainClassName(): string {
        return "lg-icon";
    }

    private _composeClass(): void {
        const list = [this._getMainClassName()];

        if (this._clickable) list.push("clickable");
        if (this._inline) list.push("lg-icon--inline");

        switch (this.size) {
            case "regular":
            default:
                list.push("lg-icon--regular");
                break;
            case "medium":
                list.push("lg-icon--medium");
        }

        switch (this.type) {
            case "regular":
            default:
                break;
            case "error":
                list.push("lg-icon--error ");
                break;
            case "info":
                list.push("lg-icon--info");
                break;
            case "warning":
                list.push("lg-icon--warning");
                break;
            case "success":
                list.push("lg-icon--success");
                break;
            case "highlighted":
                list.push("lg-icon--highlighted");
                break;
            case "disabled":
                list.push("lg-icon--disabled");
                break;
        }

        if (this.class) list.push(this.class);

        this._composedClass = list.join(" ");
    }

    private _parseSize(size: number | string): string {
        const asNumber = +size;
        // eslint-disable-next-line eqeqeq
        if (size == asNumber) {
            return asNumber + "px";
        }

        return asNumber.toString();
    }
}
